// EpgPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Form, Spinner, Button } from 'react-bootstrap';
import './EpgPage.css';
import { parseXmltv } from './parseEpg';
import {
    getFeedDays,
    buildDayView,
    formatDayLabel,
    CHANNEL_COL_WIDTH,
} from './epgModel';

function EpgPage() {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const { deviceName: routeDeviceName } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [deviceName, setDeviceName] = useState(
        routeDeviceName || searchParams.get('device') || '',
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [parsed, setParsed] = useState(null);
    const [selectedDay, setSelectedDay] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const days = useMemo(() => getFeedDays(parsed), [parsed]);
    const activeDay =
        selectedDay && days.includes(selectedDay) ? selectedDay : days[0] || '';

    const dayView = useMemo(
        () => (parsed && activeDay ? buildDayView(parsed, activeDay) : null),
        [parsed, activeDay],
    );

    const visibleRows = useMemo(() => {
        if (!dayView) {
            return [];
        }
        const needle = searchQuery.trim().toLowerCase();
        if (!needle) {
            return dayView.rows;
        }
        return dayView.rows.filter(
            ({ channel }) =>
                (channel.name || '').toLowerCase().includes(needle) ||
                (channel.id || '').toLowerCase().includes(needle),
        );
    }, [dayView, searchQuery]);

    const timelineWidthPx = dayView ? dayView.widthPx : 0;
    const bodyWidthPx = CHANNEL_COL_WIDTH + timelineWidthPx;

    const loadEpg = useCallback(
        (name) => {
            if (!name.trim()) {
                setError('Enter a device name to load its EPG.');
                return;
            }
            setLoading(true);
            setError(null);
            fetch(
                `${baseUrl}/device/website-epg/${encodeURIComponent(name)}`,
                { credentials: 'include' },
            )
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(
                            response.status === 404
                                ? 'Device not found.'
                                : `Failed to load the EPG (status ${response.status}).`,
                        );
                    }
                    return response.text();
                })
                .then((text) => {
                    const result = parseXmltv(text);
                    if (result.channels.length === 0) {
                        setError(
                            'No channels with EPG data were available for this device.',
                        );
                        setParsed(null);
                        return;
                    }
                    setParsed(result);
                })
                .catch((err) => setError(err.message))
                .finally(() => setLoading(false));
        },
        [baseUrl],
    );

    useEffect(() => {
        const initialName = routeDeviceName || searchParams.get('device') || '';
        if (searchParams.has('password')) {
            const safeParams = new URLSearchParams(searchParams);
            safeParams.delete('password');
            setSearchParams(safeParams, { replace: true });
        }
        if (initialName) {
            setDeviceName(initialName);
            loadEpg(initialName);
        }
        // Auto-load only on mount / device-name change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeDeviceName]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!deviceName.trim()) {
            return;
        }
        const params = new URLSearchParams();
        params.set('device', deviceName);
        setSearchParams(params, { replace: true });
        loadEpg(deviceName);
    };

    return (
        <div className="epg-page">
            <div className="epg-toolbar">
                <Form className="epg-load-form" onSubmit={handleSubmit}>
                    <Form.Control
                        type="text"
                        placeholder="Device name"
                        aria-label="Device name"
                        value={deviceName}
                        onChange={(e) => setDeviceName(e.target.value)}
                    />
                    <Button type="submit" variant="primary">
                        Load
                    </Button>
                </Form>

                {dayView && days.length > 0 && (
                    <div className="epg-day-controls">
                        <Form.Control
                            as="select"
                            aria-label="Select day"
                            value={activeDay}
                            onChange={(e) => setSelectedDay(e.target.value)}
                        >
                            {days.map((day) => (
                                <option key={day} value={day}>
                                    {formatDayLabel(day)}
                                </option>
                            ))}
                        </Form.Control>
                        <Form.Control
                            type="search"
                            placeholder="Search channels..."
                            aria-label="Search channels"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                )}

                {loading && (
                    <span className="epg-loading">
                        <Spinner size="sm" className="me-2" />
                        Loading EPG...
                    </span>
                )}
            </div>

            {error && (
                <Alert variant="danger" className="epg-error">
                    {error}
                </Alert>
            )}

            {dayView && (
                <div
                    className="epg-scroll"
                    role="region"
                    aria-label="TV schedule"
                    tabIndex={0}
                >
                    {visibleRows.length === 0 ? (
                        <div className="epg-empty">
                            {searchQuery
                                ? 'No channels match your search.'
                                : 'No channels available for this day.'}
                        </div>
                    ) : (
                        <div className="epg-body" style={{ width: bodyWidthPx }}>
                            <div className="epg-grid-header">
                                <div className="epg-corner">Channel</div>
                                <div
                                    className="epg-timeline-header"
                                    style={{ width: timelineWidthPx }}
                                >
                                    {dayView.timeTicks.map((tick) => (
                                        <div
                                            key={`tick-${tick.label}`}
                                            className="epg-tick"
                                            style={{ left: tick.px }}
                                        >
                                            {tick.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {visibleRows.map(({ channel, programmes }) => (
                                <div className="epg-row" key={channel.id}>
                                    <div className="epg-channel-cell">
                                        {channel.name}
                                    </div>
                                    <div
                                        className="epg-progs"
                                        style={{ width: timelineWidthPx }}
                                    >
                                        {programmes.map((prog, index) => (
                                            <div
                                                key={`${channel.id}-${index}`}
                                                className="epg-prog"
                                                title={`${prog.title} (${prog.startLabel} - ${prog.stopLabel})${prog.description ? `\n\n${prog.description}` : ''}`}
                                                style={{
                                                    left: prog.startPx,
                                                    width: prog.widthPx,
                                                }}
                                            >
                                                <span className="epg-prog-time">
                                                    {prog.startLabel}
                                                </span>
                                                <span className="epg-prog-title">
                                                    {prog.title}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default EpgPage;
